import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-scroll";
import { FaArrowDown, FaRocket, FaStar, FaTimes, FaArrowRight, FaCheckCircle, FaPlay, FaGift } from "react-icons/fa";

const typingWords = ["Innovation", "Technology", "Solutions", "Excellence", "Growth"];

const particles = Array.from({ length: 15 }, (_, i) => ({
  id: i,
  size: Math.random() * 5 + 2,
  left: Math.random() * 100,
  duration: Math.random() * 15 + 10,
  delay: Math.random() * 10,
  color: i % 2 === 0 ? "#f59e0b" : "#d97706",
}));

const orbitServices = [
  {
    img: "/images/web-dev.jpeg",
    label: "Web Dev",
    color: "#f59e0b",
    desc: "Modern, fast & scalable websites built with React.js & Node.js.",
    features: ["React.js Frontend", "Node.js Backend", "SEO Optimized", "Mobile Responsive"],
    price: "Starting ₹24,999",
    scrollTo: "services",
  },
  {
    img: "/images/mobile-app.jpeg",
    label: "Mobile App",
    color: "#d97706",
    desc: "Cross-platform Flutter apps for Android & iOS.",
    features: ["Flutter Framework", "Android & iOS", "Firebase Backend", "Push Notifications"],
    price: "Starting ₹34,999",
    scrollTo: "services",
  },
  {
    img: "/images/digital-marketing.jpeg",
    label: "Marketing",
    color: "#ef4444",
    desc: "SEO, Google Ads & Social Media to grow your business online.",
    features: ["SEO & SEM", "Google Ads", "Meta Ads", "Email Campaigns"],
    price: "Starting ₹12,999/mo",
    scrollTo: "services",
  },
  {
    img: "/images/business-strategy.jpeg",
    label: "Strategy",
    color: "#f59e0b",
    desc: "Expert consulting to plan, grow & scale your business.",
    features: ["Business Planning", "Market Research", "Competitor Analysis", "Growth Roadmap"],
    price: "Starting ₹19,999",
    scrollTo: "services",
  },
  {
    img: "/images/domain-hosting.jpeg",
    label: "Hosting",
    color: "#10b981",
    desc: "Domain registration, hosting, SSL & business email setup.",
    features: ["Domain Registration", "Web Hosting", "SSL Certificate", "Business Email"],
    price: "Starting ₹2,999/yr",
    scrollTo: "services",
  },
  {
    img: "/images/social-media.jpeg",
    label: "Social Media",
    color: "#fbbf24",
    desc: "Complete social media handling — content, posting & growth.",
    features: ["Content Creation", "Daily Posting", "Ad Campaigns", "Growth Analytics"],
    price: "Starting ₹7,999/mo",
    scrollTo: "services",
  },
];

function OrbitRing({ items, paused, onHover, onClick, radius, size }) {
  const imgSize = radius < 100 ? 40 : radius < 130 ? 48 : 56;
  const half = imgSize / 2 + 10;

  return (
    <motion.div
      style={{ position: "absolute", inset: 0 }}
      animate={{ rotate: paused ? undefined : 360 }}
      transition={paused ? { duration: 0 } : { duration: 30, repeat: Infinity, ease: "linear" }}
    >
      {items.map((item, index) => {
        const angle = (index * 360) / items.length - 90;
        const rad = (angle * Math.PI) / 180;
        const x = Math.cos(rad) * radius;
        const y = Math.sin(rad) * radius;
        return (
          <motion.div
            key={item.label}
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              x: x - half,
              y: y - half,
              width: half * 2,
            }}
          >
            <motion.div
              animate={{ rotate: paused ? undefined : -360 }}
              transition={paused ? { duration: 0 } : { duration: 30, repeat: Infinity, ease: "linear" }}
              className="cursor-pointer flex flex-col items-center"
              whileHover={{ scale: 1.2 }}
              onHoverStart={() => onHover(true)}
              onHoverEnd={() => onHover(false)}
              onClick={() => onClick(item)}
            >
              <div
                style={{
                  width: imgSize,
                  height: imgSize,
                  border: `2px solid ${item.color}80`,
                  background: "rgba(10,15,30,0.8)",
                  borderRadius: "50%",
                  overflow: "hidden",
                }}
              >
                <img src={item.img} alt={item.label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
              <span
                className="font-semibold whitespace-nowrap mt-0.5"
                style={{ color: item.color, textShadow: "0 0 8px rgba(0,0,0,0.9)", fontSize: radius < 110 ? "9px" : "11px" }}
              >
                {item.label}
              </span>
            </motion.div>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

export default function Hero() {
  const [currentWord, setCurrentWord] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [paused, setPaused] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const word = typingWords[currentWord];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(word.substring(0, displayText.length + 1));
        if (displayText.length === word.length) setTimeout(() => setIsDeleting(true), 1500);
      } else {
        setDisplayText(word.substring(0, displayText.length - 1));
        if (displayText.length === 0) {
          setIsDeleting(false);
          setCurrentWord((prev) => (prev + 1) % typingWords.length);
        }
      }
    }, isDeleting ? 80 : 120);
    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWord]);

  return (
    <section id="home" className="relative min-h-screen flex items-center hero-gradient overflow-hidden">
      {particles.map((p) => (
        <div key={p.id} className="particle" style={{
          width: p.size, height: p.size, left: `${p.left}%`,
          background: p.color, animationDuration: `${p.duration}s`, animationDelay: `${p.delay}s`,
        }} />
      ))}

      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `linear-gradient(rgba(14,165,233,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.5) 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }} />

      <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/10 rounded-full blur-3xl" />

      {/* Background circle logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img src="/DreamGoldLogo.png" alt=""
          className="w-[420px] sm:w-[600px] lg:w-[750px] h-[420px] sm:h-[600px] lg:h-[750px] object-contain rounded-full select-none"
          style={{ opacity: 0.04, filter: "blur(1px) saturate(1.5)" }} />
      </div>
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[440px] sm:w-[620px] lg:w-[770px] h-[440px] sm:h-[620px] lg:h-[770px] rounded-full"
          style={{ border: "1px solid rgba(14,165,233,0.08)" }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-28 pb-16 grid lg:grid-cols-2 gap-6 lg:gap-10 items-center w-full">
        <div className="text-center lg:text-left">
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-4">
            We Drive<br />
            <span className="gradient-text typing-cursor">{displayText}</span>
          </motion.h1>

          <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-base sm:text-lg leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
            Dream Gold Trading builds cutting-edge digital solutions that transform businesses. From web development to digital marketing, we deliver excellence at every step.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start">
            <Link to="services" smooth duration={600} offset={-80}>
              <motion.button 
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(14,165,233,0.3)" }} 
                whileTap={{ scale: 0.95 }}
                className="btn-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full text-white font-semibold text-base sm:text-lg flex items-center gap-2 relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <FaRocket className="text-sm relative z-10 group-hover:rotate-12 transition-transform duration-300" />
                <span className="relative z-10">Explore Services</span>
              </motion.button>
            </Link>
            
            <Link to="contact" smooth duration={600} offset={-80}>
              <motion.button 
                whileHover={{ scale: 1.05 }} 
                whileTap={{ scale: 0.95 }}
                className="relative group overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="glass px-6 sm:px-8 py-3 sm:py-4 rounded-full text-white font-semibold text-base sm:text-lg hover:bg-white/10 transition-all duration-300 flex items-center gap-3 relative z-10 h-[52px] sm:h-[60px]">
                  <div className="relative">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 gradient-bg rounded-full flex items-center justify-center flex-shrink-0 group-hover:rotate-12 transition-transform duration-300">
                      <FaPlay className="text-white text-xs ml-0.5"/>
                    </div>
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                  </div>
                  <div className="flex flex-col items-start justify-center flex-1">
                    <span className="text-sm sm:text-base font-bold leading-tight">Get Started</span>
                    <span className="text-xs text-gray-400 group-hover:text-green-400 transition-colors leading-tight">Free Consultation</span>
                  </div>
                  <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-yellow-500/20 rounded-full">
                    <FaGift className="text-yellow-400 text-xs" />
                    <span className="text-yellow-400 text-xs font-semibold">FREE</span>
                  </div>
                </div>
              </motion.button>
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex gap-6 sm:gap-8 mt-10 justify-center lg:justify-start">
            {[
              { value: "100+", label: "Projects Done" },
              { value: "100+", label: "Happy Clients" },
              { value: "2 Yrs", label: "Experience" },
            ].map((stat) => (
              <div key={stat.label} className="text-center lg:text-left">
                <div className="text-xl sm:text-2xl font-bold gradient-text">{stat.value}</div>
                <div className="text-gray-400 text-xs sm:text-sm">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex items-center gap-3 mt-6 justify-center lg:justify-start">
            <div className="flex -space-x-2">
              {["#f59e0b", "#d97706", "#fbbf24", "#92400e"].map((c, i) => (
                <div key={i} className="w-7 h-7 rounded-full border-2 border-dark flex items-center justify-center text-xs font-bold" style={{ background: c }}>
                  {["A", "S", "M", "E"][i]}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[...Array(4)].map((_, i) => <FaStar key={i} className="text-yellow-400 text-xs" />)}
              <FaStar className="text-yellow-400 text-xs" style={{ clipPath: "inset(0 50% 0 0)" }} />
            </div>
            <span className="text-gray-400 text-xs">100+ happy clients</span>
          </motion.div>
        </div>

        {/* Right - Orbit */}
        <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative flex items-center justify-center mt-8 lg:mt-0">
          {(() => {
            const size = typeof window !== "undefined"
              ? window.innerWidth < 400 ? 240
              : window.innerWidth < 640 ? 270
              : window.innerWidth < 768 ? 300
              : window.innerWidth < 1024 ? 320
              : window.innerWidth < 1280 ? 340 : 380
              : 340;
            const radius = size * 0.46;
            return (
              <div style={{ width: size, height: size }} className="relative">
                <div className="absolute inset-0 gradient-bg rounded-full opacity-20 blur-2xl" />
                <div className="absolute inset-0 border border-yellow-500/20 rounded-full animate-spin" style={{ animationDuration: "30s" }} />
                <div className="absolute inset-0 border border-amber-500/15 rounded-full animate-spin"
                  style={{ animationDuration: "22s", animationDirection: "reverse", margin: "6%" }} />

                {/* Center logo */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="glass rounded-full flex items-center justify-center floating shadow-xl"
                    style={{ width: size * 0.48, height: size * 0.48 }}>
                    <img src="/DreamGoldLogo.png" alt="ONexvora"
                      className="object-contain rounded-full"
                      style={{ width: size * 0.48 * 0.85, height: size * 0.48 * 0.85 }} />
                  </div>
                </div>

                {paused && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 pointer-events-none">
                    <span className="text-xs text-gray-400 bg-black/70 px-2 py-0.5 rounded-full whitespace-nowrap">Tap to view details</span>
                  </div>
                )}

                <OrbitRing items={orbitServices} paused={paused} onHover={setPaused} onClick={setSelected} radius={radius} size={size} />
              </div>
            );
          })()}
        </motion.div>
      </div>

      {/* Service Detail Popup */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.85, y: 30 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm glass rounded-3xl p-6 shadow-2xl border border-white/10"
              style={{ background: "rgba(10,15,30,0.97)" }}
            >
              <button onClick={() => setSelected(null)}
                className="absolute top-4 right-4 w-8 h-8 glass rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                <FaTimes className="text-xs" />
              </button>

              {/* Image */}
              <div className="w-full h-36 rounded-2xl overflow-hidden mb-4">
                <img src={selected.img} alt={selected.label} className="w-full h-full object-cover" />
              </div>

              {/* Title */}
              <h3 className="text-white font-black text-xl mb-1">{selected.label}</h3>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">{selected.desc}</p>

              {/* Features */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {selected.features.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <FaCheckCircle className="text-xs flex-shrink-0" style={{ color: selected.color }} />
                    <span className="text-gray-300 text-xs">{f}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <div className="rounded-xl px-4 py-2 mb-4 inline-block"
                style={{ background: `${selected.color}20`, border: `1px solid ${selected.color}40` }}>
                <span className="font-bold text-sm" style={{ color: selected.color }}>{selected.price}</span>
              </div>

              {/* CTA */}
              <Link to={selected.scrollTo} smooth duration={600} offset={-80} onClick={() => setSelected(null)}>
                <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2"
                  style={{ background: selected.color }}>
                  View Full Details <FaArrowRight className="text-xs" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-400 cursor-pointer">
        <span className="text-xs">Scroll Down</span>
        <FaArrowDown className="text-blue-400" />
      </motion.div>
    </section>
  );
}
